import Vue from "vue";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
// @ts-expect-error - vue-grid-layout no tiene tipos definidos
import VueGridLayout from "vue-grid-layout";
// @ts-expect-error - vue-echarts no tiene tipos definidos
import ECharts from "vue-echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/pie";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";
import "echarts/lib/component/title";
import { DashboardTemplateResponse } from "@/modules/DashboardTemplate/models/DashboardTemplateResponse";
import { mapGetters } from "vuex";
import {
  CollectionsOptions,
  EventChart,
  SectionOfDashboard
} from "@/modules/DashboardTemplate/models/SectionOfDashboard";
import Charts from "../../Charts/Charts.vue";
import { IndicatorOrTag } from "@/modules/DashboardTemplate/models/IndicatorOrTag";
import { InnerLayoutItem } from "@/modules/DashboardTemplate/models/InnerLayoutItem";
import { ParentLayoutItem } from "@/modules/DashboardTemplate/models/ParentLayoutItem";
import IndicatorView from "../../IndicatorView/IndicatorView.vue";
import { BqDashboardService } from "@/modules/DashboardTemplate/services/BqDashboardService";
import { ViewsConfigResponse } from "@/modules/DashboardTemplate/models/viewsConfigResponse";
import { AxiosError } from "axios";
import { Http } from "@status/codes";
import { FORM_MESSAGES } from "@/shared/constants/FormConstants";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import cloneDeep from "lodash/cloneDeep";
import { Field } from "@/modules/DashboardTemplate/models/Field";
import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import { Query } from "@/modules/DashboardTemplate/models/Query";
import store from "@/store";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import FilterSectionModal from "../../FilterSectionModal/FilterSectionModal.vue";
import { FilterItemOfQuery } from "@/modules/DashboardTemplate/models/FilterItemOfQuery";
import {
  buildAndExecuteQuery,
  buildFilters,
  fixQuery,
  prepareData
} from "@/modules/DashboardTemplate/Utils";
import { CollectionFilter } from "@/modules/DashboardTemplate/models/CollectionFilter";
import {
  BIGQUERY_CACHE_TYPES,
  DASHBOARD_TEMPLATE_EVENTS
} from "@/modules/DashboardTemplate/constants/DashboardTemplatesConstants";
import FiltersModal from "../../FiltersModal/FiltersModal.vue";
import { Utils } from "@/shared/utils/utils";
import lodash from "lodash";
import { SOCKET_TOPICS } from "@/shared/constants/GeneralConstants";
import { AES, enc } from "crypto-js";
import { FilterField } from "@/modules/DashboardTemplate/models/FilterOfCollection";

type Breakpoint = "lg" | "md" | "sm" | "xs" | "xxs";

type ChartType = "bar" | "pie" | "doughnut";

@Component({
  name: "TestComponent",
  components: {
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    "v-chart": ECharts,
    Charts: Charts,
    IndicatorView: IndicatorView,
    FilterSectionModal,
    FiltersModal
  },
  computed: {
    ...mapGetters({
      sectionsOfDashboardStore: "getSectionsOfdashboard",
      eventChart: "getEventChart"
    })
  }
})
export default class DashboardGridLayout extends Vue {
  @Prop() realDashboard?: boolean;
  @Prop() dashboardTemplate?: DashboardTemplateResponse;

  @Inject() bqDashboardService!: BqDashboardService;

  modalFilterData: {
    title: string;
  } | null = { title: "" };
  collectionsData: ViewsConfigResponse[] = [];
  sectionsOfDashboardStore!: Array<SectionOfDashboard>;
  layout: ParentLayoutItem[] = [];
  initialLayout: ParentLayoutItem[] = [];
  isDraggable = true;
  isResizable = true;
  margin = [10, 10] as [number, number];
  currentSectionFilterIndex = -1;
  currentCollectionFilterIndex = -1;
  eventChart!: EventChart;

  // Responsive breakpoints y columnas
  breakpoints: Record<Breakpoint, number> = {
    lg: 1200,
    md: 996,
    sm: 768,
    xs: 480,
    xxs: 0
  };
  cols: Record<Breakpoint, number> = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
  currentBreakpoint: Breakpoint = "lg";
  rowHeight = 30;

  // Configuración para grids internos
  innerRowHeight = 20;
  innerMargin = [8, 8] as [number, number];

  // Altura mínima para grids padre (en unidades de grid)
  minParentHeight = 8; // Ajusta este valor según necesites
  originalLayout: ParentLayoutItem[] = [];
  mainLayout: ParentLayoutItem[] = [];
  refreshGoBack = 0;

  get innerColNum(): number {
    // Ajustar columnas internas según el breakpoint
    const colNum = this.cols[this.currentBreakpoint];

    if (colNum <= 2) return 1; // xxs: 1 columna (apilados)
    if (colNum <= 4) return 2; // xs: 2 columnas
    if (colNum <= 6) return 2; // sm: 2 columnas
    if (colNum <= 10) return 3; // md: 3 columnas
    return 4; // lg: 4 columnas
  }

  // Calcular innerRowHeight dinámicamente basado en la altura del grid padre
  getInnerRowHeight(parentItem: ParentLayoutItem): number {
    if (!parentItem.innerLayout || parentItem.innerLayout.length === 0) {
      return this.innerRowHeight; // Valor por defecto si no hay inner items
    }

    // Altura del padre en pixels (restando header y padding)
    const headerHeight = 36; // Altura del header según SCSS
    const gridItemPadding = 10; // Padding del grid-item-content (5px top + 5px bottom)
    const innerGridPadding = 10; // Padding del .inner-grid (5px top + 5px bottom)

    const parentHeightPx =
      parentItem.h * this.rowHeight -
      headerHeight -
      gridItemPadding -
      innerGridPadding;

    // Encontrar la altura máxima necesaria para los inner items (y + h)
    const maxInnerHeight = Math.max(
      ...parentItem.innerLayout.map(item => item.y + item.h)
    );

    // Calcular row-height proporcional
    // Dividir la altura disponible entre la altura total de los items
    const calculatedRowHeight = Math.floor(parentHeightPx / maxInnerHeight);

    // Asegurar un mínimo razonable
    return Math.max(10, calculatedRowHeight);
  }

  // Calcular máximo número de filas que caben en el grid padre
  getMaxInnerRows(parentItem: ParentLayoutItem): number {
    const headerHeight = 36; // Altura del header (min-height del .grid-item-header)
    const innerGridPadding = 10; // Padding del .inner-grid (5px * 2)

    // Altura disponible en pixels
    const parentHeightPx =
      parentItem.h * this.rowHeight - headerHeight - innerGridPadding;

    const marginVertical = this.innerMargin[1]; // 8px

    // Calcular cuántas filas completas caben usando innerRowHeight FIJO
    // Agregamos +3 filas de buffer (balance entre llenar espacio y no exceder)
    const baseRows = Math.floor(
      (parentHeightPx + marginVertical) / (this.innerRowHeight + marginVertical)
    );

    const maxRows = baseRows + 3; // +3 filas de buffer (balance perfecto)

    return Math.max(8, maxRows); // Mínimo de 8 filas
  }

  // Verificar si un grid hijo está chocando con el borde inferior del padre
  isChildCollidingBottom(parentId: string, childId: string): boolean {
    const parentItem = this.layout.find(item => item.i === parentId);
    if (!parentItem) return false;

    const innerItem = parentItem.innerLayout.find(item => item.i === childId);
    if (!innerItem) return false;

    const maxRows = this.getMaxInnerRows(parentItem);
    const childBottomPosition = innerItem.y + innerItem.h;

    return childBottomPosition >= maxRows;
  }

  // Obtener información detallada de colisión de un hijo
  getChildCollisionInfo(parentId: string, childId: string) {
    const parentItem = this.layout.find(item => item.i === parentId);
    if (!parentItem) return null;

    const innerItem = parentItem.innerLayout.find(item => item.i === childId);
    if (!innerItem) return null;

    const maxRows = this.getMaxInnerRows(parentItem);
    const childBottomPosition = innerItem.y + innerItem.h;
    const remainingSpace = maxRows - childBottomPosition;

    return {
      parentId,
      childId,
      childY: innerItem.y,
      childH: innerItem.h,
      childBottomPosition,
      maxRows,
      isCollidingBottom: childBottomPosition >= maxRows,
      remainingSpace: Math.max(0, remainingSpace),
      canGrowVertically: remainingSpace > 0
    };
  }

  template: DashboardTemplateResponse | undefined;
  showLoadingBar = false;
  loading = false;
  showFilterSection = false;
  roundBtnClass = STYLE_CLASSES.TERTIARY_ROUND_BTN;
  indexSection = 0;
  waitingEventGraph = false;
  showFilter = true;
  mount = false;

  mounted() {
    this.initListener();
    if (this.realDashboard) {
      this.prepareAndStoreDataTemplate();
      return;
    }
    this.generateLayout();
    this.initialLayout = JSON.parse(JSON.stringify(this.layout));
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  }

  initListener() {
    this.$socket.$subscribe(
      SOCKET_TOPICS.BQ_CACHE_RESULTS_UPDATED,
      this.processBqCacheResultsUpdated
    );
  }

  processBqCacheResultsUpdated(payload: string) {
    const payloadBytes = AES.decrypt(
      payload,
      process.env.VUE_APP_TOKEN_SECRET as string
    );
    const eventData = payloadBytes.toString(enc.Utf8);
    const event: Event = JSON.parse(eventData);
    this.updateTemplateWithBqCacheResults(event);
  }

  updateTemplateWithBqCacheResults(event: Event) {
    const eventData = JSON.parse(JSON.stringify(event));
    const idQuery = eventData.idQuery;
    const result = eventData.result;

    const sections = cloneDeep(this.sectionsOfDashboardStore);

    let mustUpdate = false;
    for (const [indexSection, section] of sections.entries()) {
      if (eventData.type === BIGQUERY_CACHE_TYPES.CHART) {
        for (const [
          indexCollection,
          collection
        ] of section.collections.entries()) {
          if (
            this.validateIfMustUpdate(
              sections,
              result,
              indexSection,
              indexCollection,
              idQuery,
              collection
            )
          ) {
            sections[indexSection].collections[indexCollection].chartData = {
              chartName: collection.chartName,
              xAxis: result.xAxisValues,
              data: result.series,
              groups: result.groups ?? []
            };
            mustUpdate = true;
            break;
          }
        }
      }

      for (const [indexTag, tag] of section.tags.entries()) {
        if (
          tag.idQuery === idQuery &&
          tag.value !==
            Number((Object.values(result[0])[0] as number).toFixed(2))
        ) {
          sections[indexSection].tags[indexTag].value = Number(
            (Object.values(result[0])[0] as number).toFixed(2)
          );
          mustUpdate = true;
          break;
        }
      }
    }
    if (mustUpdate) {
      store.commit("updateSectionsOfDashboard", cloneDeep(sections));
      this.$nextTick(() => {
        this.generateLayout();
        this.emitRenderComplete();
      });
    }
  }

  validateIfMustUpdate(
    sections: SectionOfDashboard[],
    result: any,
    indexSection: number,
    indexCollection: number,
    idQuery: string,
    collection: CollectionFilter
  ) {
    if (collection.idQuery != idQuery) return false;

    const chartData =
      sections[indexSection].collections[indexCollection].chartData;

    if (!lodash.isEqual(chartData.xAxis, result.xAxisValues)) {
      return true;
    }

    if (!lodash.isEqual(chartData.data, result.series)) {
      return true;
    }

    if (!lodash.isEqual(chartData.groups, result.groups)) {
      return true;
    }

    return false;
  }

  async prepareAndStoreDataTemplate() {
    try {
      // Solo cargar datos de campos si es necesario (para modo edición)
      // En modo visualización, la plantilla ya tiene todo configurado
      if (
        !this.dashboardTemplate?.sections?.[0]?.collections?.[0]?.collection
      ) {
        await this.getCollectionsOptions();
      }

      await this.renderRealDahsboard();
      this.initialLayout = JSON.parse(JSON.stringify(this.layout));
      this.handleResize();
      window.addEventListener("resize", this.handleResize);
      this.emitRenderComplete();
    } catch (error) {
      console.error("prepareAndStoreDataTemplate error", error);
    }
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  }

  emitRenderComplete() {
    this.$emit("render-complete", cloneDeep(this.sectionsOfDashboardStore));
  }

  handleResize() {
    const width = window.innerWidth;
    let breakpoint: Breakpoint = "lg";

    if (width < this.breakpoints.xs) {
      breakpoint = "xxs";
    } else if (width < this.breakpoints.sm) {
      breakpoint = "xs";
    } else if (width < this.breakpoints.md) {
      breakpoint = "sm";
    } else if (width < this.breakpoints.lg) {
      breakpoint = "md";
    }

    if (breakpoint !== this.currentBreakpoint) {
      this.currentBreakpoint = breakpoint;
      this.reorganizeLayout();
    }
  }

  reorganizeLayout() {
    if (this.currentBreakpoint === "lg") {
      this.layout = this.mainLayout;
      return;
    }

    const colNum = this.cols[this.currentBreakpoint];

    // Determinar cuántos items caben por fila según el breakpoint
    const itemsPerRow = colNum <= 6 ? 1 : colNum <= 10 ? 2 : 3;

    // Ordenar items por posición visual (primero por fila Y, luego por columna X)
    const sortedItems = [...this.layout].sort((item1, item2) => {
      if (item1.y === item2.y) return item1.x - item2.x; // Misma fila: ordenar por columna (izq a der)
      return item1.y - item2.y; // Diferente fila: ordenar de arriba a abajo
    });

    // Acumular altura para posicionar items uno debajo del otro
    let currentY = 0;
    const itemsInCurrentRow: any[] = [];
    this.layout = sortedItems.map((item, index) => {
      const isFirstInRow = index % itemsPerRow === 0;
      const positionInRow = index % itemsPerRow;

      // Si es el primero de una nueva fila, actualizar currentY
      if (isFirstInRow && index > 0) {
        // Obtener la altura máxima de la fila anterior
        const maxHeight = Math.max(...itemsInCurrentRow.map(i => i.h));
        currentY += maxHeight;
        itemsInCurrentRow.length = 0; // Limpiar array
      }

      // Calcular x según la posición en la fila
      const x = positionInRow * Math.floor(colNum / itemsPerRow);

      // Reorganizar también los inner items cuando cambia el breakpoint
      const reorganizedInnerLayout = item.innerLayout
        ? this.reorganizeInnerItems(item.innerLayout)
        : [];

      // Calcular la altura necesaria para el grid padre basándose en los items internos
      const requiredHeight = this.calculateRequiredHeight(
        reorganizedInnerLayout
      );

      const newItem = {
        ...item,
        x: x,
        y: currentY,
        // Ajustar altura del padre para que muestre todos los hijos
        h: requiredHeight, //Math.max(item.h, requiredHeight),
        innerLayout: reorganizedInnerLayout
      };

      itemsInCurrentRow.push(newItem);
      return newItem;
    });
  }

  private calculateRequiredHeight(innerItems: InnerLayoutItem[]): number {
    if (innerItems.length === 0) return 3; // Altura mínima por defecto

    let totalInnerHeight: number;

    // Si no es pantalla lg, sumar todas las alturas (items más apilados)
    if (this.currentBreakpoint !== "lg") {
      totalInnerHeight = innerItems.reduce((sum, item) => sum + item.h, 0);
    } else {
      // Si es lg, encontrar el item que está más abajo (y + h máximo)
      totalInnerHeight = Math.max(...innerItems.map(item => item.y + item.h));
    }

    // Convertir de unidades de inner grid a unidades de grid padre
    // totalInnerHeight está en unidades de inner grid, lo convertimos a unidades del padre
    const heightInParentUnits =
      Math.ceil((totalInnerHeight * this.innerRowHeight) / this.rowHeight) + 1;

    return heightInParentUnits + 2;
  }

  get currentColNum(): number {
    return this.cols[this.currentBreakpoint];
  }

  resetLayout() {
    this.layout = JSON.parse(JSON.stringify(this.initialLayout));
    this.reorganizeLayout();
  }

  generateLayout() {
    const items: ParentLayoutItem[] = [];
    for (const [index, element] of this.sectionsOfDashboardStore.entries()) {
      items.push({
        name: element.name,
        x: element.layout?.x || 0,
        y: element.layout?.y || 0,
        w: element.layout?.width || 5,
        h: element.layout?.height || 20,
        i: index.toString(),
        indexSectionOfDashboard: index,
        innerLayout: this.generateInnerLayout(index),
        uuid: Utils.idGenerator()
      });
    }

    this.layout = items;
    this.originalLayout = lodash.cloneDeep(items);
    this.mainLayout = lodash.cloneDeep(items);
    this.showFilter = false;
  }

  generateInnerLayout(parentId: number): InnerLayoutItem[] {
    const innerItems: InnerLayoutItem[] = [];
    for (const [index, collection] of this.sectionsOfDashboardStore[
      parentId
    ].collections.entries()) {
      if (!collection.chartName) {
        continue;
      }
      innerItems.push({
        x: collection.layout?.x || 0,
        y: collection.layout?.y || 0,
        w: collection.layout?.width || 10,
        h: collection.layout?.height || 20,
        i: index.toString(),
        type: "chart",
        propertiesToChart: {
          indexSectionOfDashboard: parentId,
          indexCollectionFilter: index,
          groupBy: collection.groupBy,
          chartType: collection.chartType,
          chartColor: { hex: collection.chartColor as string }
        },
        chartType: collection.chartType as ChartType,
        indexSectionOfDashboard: 0
      });
    }

    for (const [index, tag] of this.sectionsOfDashboardStore[
      parentId
    ].tags.entries()) {
      if (!tag.show) continue;
      innerItems.push({
        x: tag.layout?.x || 0,
        y: tag.layout?.y || 0,
        w: tag.layout?.width || 5,
        h: tag.layout?.height || 2,
        i: (index + 10).toString(),
        indexSectionOfDashboard: parentId,
        indexTag: index,
        type: "tag",
        propertiesToChart: tag
      });
    }

    for (const [index, indicator] of this.sectionsOfDashboardStore[
      parentId
    ].indicators.entries()) {
      if (!indicator.show) continue;
      innerItems.push({
        indexSectionOfDashboard: parentId,
        indexIndicator: index,
        x: indicator.layout?.x || 0,
        y: indicator.layout?.y || 0,
        w: indicator.layout?.width || 5,
        h: indicator.layout?.height || 2,
        i: (index + 20).toString(),
        type: "indicator",
        propertiesToChart: indicator
      });
    }

    // Reorganizar items para que se apilen correctamente según el breakpoint actual
    return innerItems; //this.reorganizeInnerItems(innerItems);
  }

  private reorganizeInnerItems(items: InnerLayoutItem[]): InnerLayoutItem[] {
    if (items.length === 0) return items;

    const colNum = this.innerColNum;
    const itemsPerRow = colNum <= 2 ? 1 : colNum <= 3 ? 2 : colNum;

    // Acumular altura para posicionar items uno debajo del otro
    let currentY = 0;
    const itemsInCurrentRow: InnerLayoutItem[] = [];

    return items.map((item, index) => {
      const isFirstInRow = index % itemsPerRow === 0;
      const positionInRow = index % itemsPerRow;

      // Si es el primero de una nueva fila, actualizar currentY
      if (isFirstInRow && index > 0) {
        const maxHeight = Math.max(...itemsInCurrentRow.map(i => i.h));
        currentY += maxHeight;
        itemsInCurrentRow.length = 0;
      }

      // Calcular x según la posición en la fila
      const x = positionInRow * Math.floor(colNum / itemsPerRow);

      const newItem = {
        ...item,
        x: x,
        y: currentY
        // w y h se mantienen del item original
      };

      itemsInCurrentRow.push(newItem);
      return newItem;
    });
  }

  // Este método se ejecuta DESPUÉS de que vue-grid-layout actualiza el layout completo
  onParentLayoutUpdated(newLayout: ParentLayoutItem[]) {
    // Actualizar el originalLayout para que las posiciones se preserven
    this.originalLayout = cloneDeep(newLayout);
    // Enviar al store el layout actualizado
    this.$store.commit("updateLocationsSection", newLayout);
  }

  onResized(i: string, newH: number) {
    // Actualizar suavemente el layout local sin re-crear componentes
    this.$nextTick(() => {
      const itemIndex = this.layout.findIndex(item => item.i === i);
      if (itemIndex !== -1) {
        const parentItem = this.layout[itemIndex];

        // Calcular el maxRows REAL (sin buffer) para ajustar hijos al reducir padre
        const headerHeight = 36;
        const innerGridPadding = 10;
        const parentHeightPx =
          newH * this.rowHeight - headerHeight - innerGridPadding;
        const marginVertical = this.innerMargin[1];

        // Cálculo estricto sin buffer para el auto-ajuste
        const strictMaxRows = Math.floor(
          (parentHeightPx + marginVertical) /
            (this.innerRowHeight + marginVertical)
        );

        // Ajustar los hijos si exceden el nuevo límite ESTRICTO
        if (parentItem.innerLayout) {
          parentItem.innerLayout.forEach(innerItem => {
            const childBottom = innerItem.y + innerItem.h;

            // Si el hijo excede el límite estricto, ajustarlo
            if (childBottom > strictMaxRows) {
              const newChildH = Math.max(2, strictMaxRows - innerItem.y);
              this.$set(innerItem, "h", newChildH);
            }
          });
        }

        // Actualizar altura del item
        this.$set(this.layout, itemIndex, {
          ...this.layout[itemIndex],
          h: newH
        });
      }
    });
  }

  onMove(i: string, newX: number, newY: number) {
    console.log("onMove ---->", i, newX, newY);
    // No hacer nada aquí, onParentLayoutUpdated se encarga de todo
  }

  // Eventos para grids internos
  onInnerResize(parentId: string, childId: string, newH: number, newW: number) {
    console.log("onInnerResize ---->", parentId, childId, newH, newW);
    // Buscar el parent item
    const parentItem = this.layout.find(item => item.i === parentId);
    if (!parentItem) return;

    // Buscar el inner item
    const innerItem = parentItem.innerLayout.find(item => item.i === childId);
    if (!innerItem) return;

    // Calcular el máximo de filas disponibles en el padre
    const maxRows = this.getMaxInnerRows(parentItem);

    // Calcular la posición inferior del hijo (y + h)
    const childBottomPosition = innerItem.y + newH;

    // Detectar colisión con el borde inferior (solo para logging/debugging)
    const isCollidingBottom = childBottomPosition >= maxRows;

    if (isCollidingBottom) {
      console.log(
        `✅ Grid hijo ${childId} llegó al límite inferior del padre ${parentId}`
      );
    }
  }

  onInnerLayoutUpdated(parentId: string, newLayout: InnerLayoutItem[]) {
    this.$store.commit("updateLocationsAndSizes", newLayout);
  }

  onChartTypeChanged() {
    console.log("onChartTypeChanged");
  }

  openFilter(indexSection: number, indexCollection: number) {
    this.currentSectionFilterIndex = indexSection;
    this.currentCollectionFilterIndex = indexCollection;
    this.showFilter = true;
  }

  async getCollectionsOptions() {
    try {
      const viewsConfig = await this.bqDashboardService.getViesConfig();

      if (viewsConfig.data) {
        this.collectionsData = viewsConfig.data;
        return;
      }
      Snackbar.popInfo(viewsConfig.message);
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  @Watch("dashboardTemplate")
  async renderRealDahsboard() {
    this.template = cloneDeep(this.dashboardTemplate);
    const sections = this.template?.sections ? this.template?.sections : [];
    this.showLoadingBar = true;
    // Actualizar el store con la plantilla inicial (sin datos de queries)
    store.commit(
      "updateSectionsOfDashboard",
      cloneDeep(this.template?.sections)
    );
    this.generateLayout();
    // Ejecutar todas las secciones en paralelo
    const sectionPromises = sections.map((element, indexSection) =>
      this.getSection(indexSection, element).then(async () => {
        // Esperar a que Vue actualice el store antes de regenerar layout
        await this.$nextTick();
        this.generateLayout();
      })
    );

    await Promise.all(sectionPromises);
    this.showLoadingBar = false;
    this.emitRenderComplete();
  }

  async getSection(indexSection: number, element: SectionOfDashboard) {
    try {
      const collectionPromises = element.collections.map(
        async (collection: any, indexCollection: any) => {
          if (this.collectionsData.length > 0) {
            this.getfieldsOfEveryCollection(indexSection, indexCollection);
          }
          if (!collection.query || Object.keys(collection.query).length === 0)
            return;
          await this.getData(
            collection.query as Query,
            indexSection,
            indexCollection,
            collection.chart.chartName
          );
        }
      );
      await Promise.all(collectionPromises);

      const promiseTags = [];
      for (const [indexTag, tag] of element.tags.entries()) {
        if (!tag) {
          console.warn(`Tag en índice ${indexTag} es null/undefined`);
          continue;
        }

        if (!tag.query || Object.keys(tag.query).length === 0) {
          console.warn(
            `Tag "${tag.indicatorOrTagName}" (sección ${indexSection}, índice ${indexTag}) no tiene query - quedará undefined`
          );
          continue;
        }

        promiseTags.push(
          this.queryTag(
            tag,
            indexSection,
            indexTag,
            tag.query!, // (element.collections || [])?.[0]?.query as Query,
            tag.view as string
          )
        );
      }
      await Promise.all(promiseTags);

      // Procesar indicadores después de que las colecciones y tags estén listos
      (element.indicators || []).forEach((indicator, indexIndicator) => {
        const result = this.resolveIndicator(
          indexSection,
          indicator.formulaToCreateIndicator
        );
        this.template!.sections[indexSection].indicators[
          indexIndicator
        ].value = Number(result?.toFixed(2) ?? 0);
      });

      // Actualizar el store UNA SOLA VEZ cuando toda la sección terminó
      this.updateDataInStore();
    } catch (e) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  getfieldsOfEveryCollection(indexSection: number, indexCollection: number) {
    this.template!.sections[indexSection].collectionsOptions = (cloneDeep(
      this.collectionsData
    ) as unknown) as CollectionsOptions[];
    this.template!.sections[indexSection].collections[
      indexCollection
    ].collection = [
      (this.collectionsData as [{ id: ""; name: ""; fields: [] }]).find(
        item =>
          item.id ==
          this.template!.sections[indexSection].collections[indexCollection].id
      ) as { id: string; name: string; fields: Field[] }
    ];
    this.template!.sections[indexSection].collections[
      indexCollection
    ].groupBy = this.fixGroupBy(
      (this.template!.sections[indexSection].collections[indexCollection]
        .collection[0].fields as unknown) as DynamicSelectData[],
      this.template!.sections[indexSection].collections[indexCollection].chart
        .groupBy
    );
    this.template!.sections[indexSection].collections[
      indexCollection
    ].seeBy = this.template!.sections[indexSection].collections[
      indexCollection
    ].chart.seeBy;
    this.template!.sections[indexSection].collections[
      indexCollection
    ].chartName = this.template!.sections[indexSection].collections[
      indexCollection
    ].chart.chartName;
  }

  fixGroupBy(fields: DynamicSelectData[], groupBy: string[]) {
    return groupBy.map(groupField => {
      const field = fields.find(item => item.field === groupField);
      return {
        id: field?.field ?? groupField,
        name: field?.field ?? "",
        type: field?.type ?? ""
      };
    });
  }

  async getData(
    query: Query,
    indexSection: number,
    indexCollection: number,
    chartName: string
  ) {
    try {
      this.loading = true;
      const allFiltersOfSection = this.sectionsOfDashboardStore[
        indexSection
      ].collections.flatMap(collection => collection.filters);

      const documentIndex = allFiltersOfSection.findIndex(
        filter => (filter.field?.[0] as FilterField)?.id === "document"
      );
      if (documentIndex !== -1) {
        const [documentFilter] = allFiltersOfSection.splice(documentIndex, 1);
        allFiltersOfSection.push(documentFilter);
      }
      const queryFixed = fixQuery(
        query,
        allFiltersOfSection as FilterItemOfQuery[]
      );

      const response = await this.getDataToChart(queryFixed);
      this.loading = false;

      const chartData = {
        chartName,
        xAxis: response.data.result.xAxisValues,
        data: response.data.result.series,
        groups: response.data.result.groups ?? []
      };
      this.template!.sections[indexSection].collections[
        indexCollection
      ].idQuery = response.data.idQuery;
      this.template!.sections[indexSection].collections[
        indexCollection
      ].chartData = chartData;
    } catch (error) {
      console.error("Error getData -->", error);
    }
  }

  async getDataToChart(query: any) {
    const response = await this.bqDashboardService.getDataToChart(query);
    if (!response.data.result) {
      response.data = {
        xAxis: [],
        data: [],
        groups: {}
      };
    }
    return response;
  }

  updateDataInStore() {
    store.commit(
      "updateSectionsOfDashboard",
      cloneDeep(this.template?.sections)
    );
    this.$nextTick(() => {
      this.generateLayout();
    });
  }

  async queryTag(
    tag: IndicatorOrTag,
    indexSection: number,
    indexTag: number,
    query: Query,
    view: string
  ) {
    try {
      const tagQuery = cloneDeep(query);

      const selectorToOperate =
        tag.selectorFieldToOperate && tag.selectorFieldToOperate.length > 0
          ? tag.selectorFieldToOperate[0].id
          : "";

      const alias = selectorToOperate
        ? selectorToOperate
        : (tag.field as string);

      tagQuery.selectFields.agregations = [
        {
          type: tag.operation as string,
          view: view,
          field: tag.field as string,
          alias: alias,
          selector: selectorToOperate
        }
      ];
      tagQuery.groupFields = [];

      const allFiltersOfSection = this.sectionsOfDashboardStore[
        indexSection
      ].collections.flatMap(collection => collection.filters);

      const documentIndex = allFiltersOfSection.findIndex(
        filter => (filter.field?.[0] as FilterField)?.id === "document"
      );
      if (documentIndex !== -1) {
        const [documentFilter] = allFiltersOfSection.splice(documentIndex, 1);
        allFiltersOfSection.push(documentFilter);
      }

      const tagQueryFixed = fixQuery(
        tagQuery,
        allFiltersOfSection as FilterItemOfQuery[]
      );
      const response = await this.getDataToChart(tagQueryFixed);
      if (
        !Array.isArray(response.data.result) ||
        response.data.result.length == 0
      ) {
        console.warn(`Tag "${tag.indicatorOrTagName}" no retornó datos`);
        this.template!.sections[indexSection].tags[indexTag].value = 0;
        return;
      }

      // Intentar obtener el valor con alias primero, luego con field
      let value = response.data.result[0][alias];
      if (value === undefined) {
        value = response.data.result[0][tag.field as string];
      }

      // Redondear a 2 decimales
      const finalValue =
        typeof value === "number" ? Number(value.toFixed(2)) : value ?? 0;

      this.template!.sections[indexSection].tags[indexTag].idQuery =
        response.data.idQuery;
      this.template!.sections[indexSection].tags[indexTag].value = finalValue;
    } catch (error) {
      console.error(`Error procesando tag "${tag.indicatorOrTagName}":`, error);
      this.template!.sections[indexSection].tags[indexTag].value = 0;
    }
  }

  resolveIndicator(indexSection: number, expresion: any) {
    let resultado = null;
    let operador = null;

    const valores = this.template!.sections[indexSection].tags.map(e => {
      return { id: e.indicatorOrTagName, value: e.value };
    });

    for (const item of expresion) {
      const token = item.id;
      const valueTag = valores.find(e => e.id == token);
      if (valueTag) {
        if (resultado === null) {
          resultado = valueTag.value;
        } else if (operador) {
          resultado = this.operar(resultado, operador, valueTag.value);
          operador = null;
        }
      } else {
        operador = token;
      }
    }

    return resultado;
  }

  operar(a: any, operador: any, b: any) {
    switch (operador) {
      case "+":
        return a + b;
      case "-":
        return a - b;
      case "*":
        return a * b;
      case "/":
        return b !== 0 ? a / b : null;
      default:
        throw new Error(`Operador no soportado: ${operador}`);
    }
  }

  async makeQuery(
    indexSection: number,
    indexCollection: number,
    isSave: boolean
  ) {
    if (!isSave) {
      if (!this.waitingEventGraph) {
        return;
      }
      this.waitingEventGraph = false;
    }
    this.showFilter = false;
    this.showLoadingBar = true;
    try {
      let filters: FilterItemOfQuery[] = [];
      const collections =
        this.sectionsOfDashboardStore[indexSection].collections || [];
      filters = buildFilters(
        collections,
        filters,
        indexCollection,
        this.sectionsOfDashboardStore,
        indexSection,
        false
      );
      await Promise.all([
        this.buildEveryQuery(
          collections,
          filters,
          indexCollection,
          indexSection
        ),
        this.updateTagsQueries(
          this.sectionsOfDashboardStore[indexSection].tags,
          filters,
          indexSection
        )
      ]);
      this.generateLayout();
    } catch (e) {
      console.error(e);
    }
    this.showLoadingBar = false;
    this.emitRenderComplete();
  }

  makeQueryWithAllFiltersSection() {
    this.showFilterSection = false;
    this.makeQuery(this.indexSection, -1, true);
  }

  openFilterSection(item: ParentLayoutItem) {
    this.indexSection = parseInt(item.i);
    this.showFilterSection = true;
  }

  async buildEveryQuery(
    collections: CollectionFilter[],
    filters: FilterItemOfQuery[],
    indexCollectionFilter: number,
    indexSection: number
  ) {
    const promises = collections.map((collection, indexCollection) =>
      this.manageAsynQuerys(
        collection,
        filters,
        indexCollection,
        indexCollectionFilter,
        indexSection
      )
    );

    await Promise.all(promises);
  }

  goBack(item: ParentLayoutItem) {
    const sections = cloneDeep(this.template?.sections) as SectionOfDashboard[];
    const section = sections[item.indexSectionOfDashboard];
    sections[item.indexSectionOfDashboard] = section;
    store.commit("updateSectionsOfDashboard", sections);
    this.$nextTick(() => {
      this.refreshGoBack++;
      this.indexSection = item.indexSectionOfDashboard;
      this.makeQueryWithAllFiltersSection();
    });
  }

  @Watch("eventChart")
  changeEventChart() {
    this.currentCollectionFilterIndex = this.eventChart.indexCollection;
    this.currentSectionFilterIndex = this.eventChart.indexSection;
    this.waitingEventGraph = true;
  }

  async manageAsynQuerys(
    collection: CollectionFilter,
    filters: FilterItemOfQuery[],
    indexCollection: number,
    indexCollectionFilter: number,
    indexSection: number
  ) {
    try {
      const groupBy = this.template!.sections[indexSection].collections[
        indexCollection
      ].groupBy as DynamicSelectData[];

      const seeBy = this.dashboardTemplate?.sections[indexSection]?.collections[
        indexCollection
      ].chart.seeBy;

      const rangeSize = this.dashboardTemplate?.sections[indexSection]
        ?.collections[indexCollection].chart.rangeSize;
      const query = buildAndExecuteQuery(
        collection,
        filters,
        indexCollection,
        indexCollectionFilter,
        groupBy,
        rangeSize,
        seeBy as DynamicSelectData[],
        true
      );
      if (!query) return;
      store.commit(DASHBOARD_TEMPLATE_EVENTS.SET_QUERY, {
        indexSectionOfDashboard: indexSection,
        indexCollectionFilter: indexCollection,
        query: query
      });
      store.commit("setFetchData", true);
      const dataToChartResponse = await this.getDataToChart(query);
      store.commit("setFetchData", false);

      if (dataToChartResponse.code !== Http.Found) {
        store.commit(DASHBOARD_TEMPLATE_EVENTS.SET_CONFIG_CHART, {
          notFound: true,
          indexSectionOfDashboard: indexSection,
          indexCollectionFilter: indexCollection
        });
      }

      this.sendDataToStoreReadyToChart(
        dataToChartResponse.data.result.xAxisValues,
        dataToChartResponse.data.result.series,
        dataToChartResponse.data.result.groups,
        indexCollection,
        groupBy,
        rangeSize,
        seeBy,
        indexSection,
        indexCollectionFilter,
        dataToChartResponse.data?.idQuery || ""
      );
    } catch (error) {
      console.error("Error manageAsynQuerys -->", error);
    }
  }

  sendDataToStoreReadyToChart(
    xAxis: [],
    data: [],
    groups: {},
    indexCollection: number,
    groupBy: DynamicSelectData[],
    rangeSize: number | null,
    seeBy: DynamicSelectData[],
    indexSection: number,
    indexCollectionFilter: number,
    idQuery: string
  ) {
    try {
      const chartName = this.dashboardTemplate?.sections[indexSection]
        ?.collections[indexCollection].chart.chartName;
      const chartColor = this.dashboardTemplate?.sections[indexSection]
        ?.collections[indexCollection].chartColor;
      const prepareDataToStore = prepareData(
        chartName as string,
        xAxis,
        data,
        groups,
        indexCollection,
        groupBy,
        rangeSize,
        seeBy,
        indexSection,
        chartColor as { hex: "" },
        indexCollectionFilter,
        idQuery
      );
      store.commit(
        DASHBOARD_TEMPLATE_EVENTS.SET_CONFIG_CHART,
        prepareDataToStore
      );
    } catch (error) {
      console.error("Error sendDataToStoreReadyToChart -->", error);
    }
  }

  async updateTagsQueries(
    tags: IndicatorOrTag[],
    filters: FilterItemOfQuery[],
    indexSection: number
  ) {
    try {
      for (const [index, tag] of tags.entries()) {
        const copyOfTag = cloneDeep(tag);

        if (!copyOfTag.query || Object.keys(copyOfTag.query).length === 0)
          continue;
        copyOfTag.query.filters = filters;

        const dataToChartResponse = await this.bqDashboardService.getDataToChart(
          copyOfTag.query
        );
        if (dataToChartResponse.code !== Http.Found) {
          continue;
        }

        copyOfTag.value = Number(
          (Object.values(
            dataToChartResponse.data.result[0]
          )[0] as number).toFixed(2)
        );

        store.commit(DASHBOARD_TEMPLATE_EVENTS.UPDATE_TAG, {
          indexSectionOfDashboard: indexSection,
          index: index,
          tag: copyOfTag
        });
      }
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }
}
